import { CREATE_ENTITY, KIND } from 'app/models/entities/constants';
import { createOptimisticAction } from 'app/remote/generic/actions';
import {
  createEntityStringId,
  EntityFields,
  EntityStringId,
  IEntity,
  IEntityType,
  IIdentifiableEntity,
} from 'app/types/entity';
import { batchActions } from 'redux-batched-actions';
import { Fields } from '@engoo/view-model';

export interface IIdAction {
  id: EntityStringId;
  payload: {
    [index: string]: unknown;
  };
  type: unknown;
}

export type EntityCreatorChild = (action: IIdAction) => EntityStringId;

/**
 * Helper for embedding entity type information into a payload.
 *
 * @returns Properties necessary to deliver a payload pertaining to operations on entities.
 */
export const entityTypePayloadProperties = <T extends IEntity>(entityType: IEntityType<T>) => ({
  [Fields.TYPE]:    entityType.typeName,
  [Fields.VERSION]: entityType.version,
  [KIND]:   entityType.kind,
});

/**
 * Factory function for generating action creators for the CREATE_ENTITY action.
 * Only works with entities that can have an ID arbitrarily created for them.
 *
 * @returns An action creator for creating entities.
 */
export const entityCreator = <T extends IIdentifiableEntity>(
  entityType: IEntityType<T>,
  defaultsFactory: (child: EntityCreatorChild) => Partial<EntityFields<T>> = () => ({}),
  metaCreator?: unknown,
): (
  properties?: Partial<EntityFields<T>>,
) => IIdAction => {
  const actionCreator = createOptimisticAction(CREATE_ENTITY, undefined, metaCreator);

  /**
   * @returns The payload to send to the redux store.
   */
  return (properties) => {
    // Create an ID if one is not provided.
    const id = properties?.id ?? createEntityStringId();
    const childActions: IIdAction[] = [];
    const child = (action: IIdAction) => {
      // If the entity creates a child entity, add it to the list of actions so that the child is
      // created as well.
      childActions.push(action);
      return action.id;
    };

    const payload = {
      id,
      ...defaultsFactory(child),
      ...properties,
      ...entityTypePayloadProperties(entityType),
    };
    const entityAction = actionCreator(payload);

    if (childActions.length === 0) {
      // Don't use a batched action if we don't need to.
      entityAction.id = id;
      return entityAction;
    } else {
      // Instead of using action.payload.id to get the new entity's id, use action.id because the
      // payload of batched actions is an array of actions, not a regular action payload.
      const batchedAction = Object.assign(
        batchActions([...childActions, entityAction]),
        { id },
      );

      return batchedAction;
    }
  };
};
